import React, { useMemo } from 'react';
import { safeParseFloat } from 'libs/end-to-end/src/math';
import { Colors } from 'libs/end-to-end/src/types/branding/color';
import { truncateTextIfNecessary } from 'libs/end-to-end/src/success-phenotype-v2-utils';
import { TableViewModeArguments } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { useGetColumnDisplayName } from 'apps/sigma2-ui/src/explore-section/hooks/useGetColumnDisplayName';
import { BarTotalPopoverLayer } from 'apps/sigma2-ui/src/components/BarTotalPopoverLayer';
import { useMetricConfig } from 'apps/sigma2-ui/src/lib/hooks/useMetricConfig';
import { determineUnitOfMetric } from 'libs/end-to-end/src/metrics';
import { S2GroupedBarChart } from 'apps/sigma2-ui/src/components/S2GroupedBarChart';
import { S2GroupedBarChartGroup } from 'apps/sigma2-ui/src/components/S2GroupedBarChart';
import { S2GroupedBarChartLevel } from 'libs/end-to-end/src/grouped-bar';
import { SingleMetricTooltipContent } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/GenericTableViewTooltip';
import { useTableViewXAxisLabel } from 'apps/sigma2-ui/src/explore-section/hooks/useTableViewXAxisLabel';
import { computeValue } from 'apps/sigma2-ui/src/explore-section/utils/TableViewUtils';

/**
 * Bar chart component for visualizing TableView data using Nivo.
 *
 * Features:
 * - Converts table data to Nivo bar chart format
 * - Vertical layout with smooth animations
 * - Custom tooltip with formatted values
 * - Consistent theming with app design system
 * - First column used as category, selected metric as value
 *
 * @example
 * ```tsx
 * <BarChartMode
 *   data={tableData}
 *   visibleCells={visibleCells}
 *   viewConfiguration={viewConfiguration}
 * />
 * ```
 */
export const BarChartMode: React.FC<TableViewModeArguments> = ({
  data,
  visibleRows,
  viewConfiguration,
}) => {
  const { selectedMetric, overallDisplayMode } = viewConfiguration;
  const { getColumnDisplayName } = useGetColumnDisplayName();

  const selectedMetricIndex = useMemo(() => {
    if (!selectedMetric) {
      return -1;
    }
    return data.columnHeaders.indexOf(selectedMetric);
  }, [selectedMetric, data.columnHeaders]);

  const { getMetricAndFocusAreaFromColumnName } = useMetricConfig();

  // Transform table data to Nivo bar chart format
  const barChartData = useMemo(() => {
    if (!selectedMetric) {
      return [];
    }

    const metricInfo = getMetricAndFocusAreaFromColumnName({
      columnName: selectedMetric,
    });

    if (!metricInfo) {
      return [];
    }

    // Handle both new and old data structures
    const columnHeaders = data.columnHeaders;
    const rows = data.rows;

    const selectedMetricIndex = columnHeaders.indexOf(selectedMetric);
    if (selectedMetricIndex === -1) {
      return [];
    }

    const barChartData = rows.map((row, rowIndex) => {
      const categoryValue = row[0]; // First column is the category (groupBy dimension)
      const metricValue = row[selectedMetricIndex + 1]; // +1 because row[0] is groupBy dimension
      const numericValue = safeParseFloat(metricValue);

      const units = determineUnitOfMetric({
        knownMaximum: null,
        isMetricBoolean: metricInfo.metric.isMetricBoolean,
        metricFocusArea: metricInfo.focusArea,
        definitionUnit: metricInfo.metric.unit,
      });

      return {
        rowIndex,
        id: categoryValue,
        label: truncateTextIfNecessary({
          text: categoryValue,
          maxChars: 17,
        }),
        value: numericValue || 0,
        rawValue: metricValue,
        [`${categoryValue}_Units`]: units,
      };
    });

    const filteredBarChartData = barChartData.filter(
      (row, index) => visibleRows.length === 0 || visibleRows.includes(index),
    );

    return filteredBarChartData;
  }, [selectedMetric, data, visibleRows, getMetricAndFocusAreaFromColumnName]);

  // Determine units for S2GroupedBarChart
  const metricUnits = useMemo(() => {
    if (viewConfiguration.units) {
      return viewConfiguration.units;
    }

    if (!selectedMetric) {
      return 'percentage';
    }

    const metricInfo = getMetricAndFocusAreaFromColumnName({
      columnName: selectedMetric,
    });

    if (!metricInfo) {
      return 'percentage';
    }

    const units = determineUnitOfMetric({
      knownMaximum: null,
      isMetricBoolean: metricInfo.metric.isMetricBoolean,
      metricFocusArea: metricInfo.focusArea,
      definitionUnit: metricInfo.metric.unit,
    });

    return units;
  }, [
    selectedMetric,
    getMetricAndFocusAreaFromColumnName,
    viewConfiguration.units,
  ]);

  const chartUnits = useMemo(() => {
    if (overallDisplayMode === 'count') {
      return 'count';
    }

    return metricUnits;
  }, [metricUnits, overallDisplayMode]);

  // Transform data for S2GroupedBarChart
  const s2ChartData = useMemo(() => {
    if (!selectedMetric || barChartData.length === 0) {
      return { xGroups: [], xLevels: [] };
    }

    const metricDisplayName = getColumnDisplayName(selectedMetric);

    // Single level since we only have one metric
    const xLevels: S2GroupedBarChartLevel[] = [
      {
        id: selectedMetric,
        label: metricDisplayName,
        color: Colors[0],
      },
    ];

    // Each bar becomes a group with one level
    const xGroups: S2GroupedBarChartGroup[] = barChartData.map((item) => {
      const rowIndex = item.rowIndex;
      const value = computeValue({
        value: item.value,
        numeratorSize:
          data.numeratorSizes[rowIndex]?.[selectedMetricIndex + 1] ?? 0,
        chartUnits,
        overallDisplayMode,
      });

      return {
        id: String(item.id),
        label: String(item.label),
        groupLevels: [
          {
            id: selectedMetric,
            label: metricDisplayName,
            value,
            [`${item.id}_Units`]: chartUnits,
            tooltip: (
              <SingleMetricTooltipContent
                metricColumn={selectedMetric}
                percentageValue={item.value}
                levelDisplayName={data.rowsMeta[rowIndex]?.groupByLevel ?? ''}
                totalSubjects={
                  data.sampleSizes[rowIndex]?.[selectedMetricIndex + 1] ?? 0
                }
                numeratorSize={
                  data.numeratorSizes[rowIndex]?.[selectedMetricIndex + 1] ?? 0
                }
                units={metricUnits}
                overallDisplayMode={overallDisplayMode}
              />
            ),
          },
        ],
      };
    });

    return { xGroups, xLevels };
  }, [
    selectedMetric,
    barChartData,
    getColumnDisplayName,
    data.numeratorSizes,
    data.rowsMeta,
    data.sampleSizes,
    selectedMetricIndex,
    chartUnits,
    overallDisplayMode,
    metricUnits,
  ]);

  const xAxisLabel = useTableViewXAxisLabel({
    data,
    viewConfiguration,
  });

  if (!selectedMetric) {
    return null;
  }

  return (
    <div className="w-full bg-white">
      <div className="h-[400px]">
        <S2GroupedBarChart
          xGroups={s2ChartData.xGroups}
          xLevels={s2ChartData.xLevels}
          yAxisLabel={getColumnDisplayName(selectedMetric)}
          xAxisLabel={xAxisLabel}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: -45,
            legend: xAxisLabel,
            legendPosition: 'middle',
            legendOffset: 100,
          }}
          units={chartUnits}
          horizontalFixedHeight={true}
          horizontalHeight={350}
          margin={{ top: 50, right: 50, bottom: 120, left: 100 }}
          layers={[BarTotalPopoverLayer]}
        />
      </div>
    </div>
  );
};
